/* eslint-disable camelcase */
import { call, put, take, select, takeLatest } from 'redux-saga/effects'
import axios from 'axios'
import { get } from 'lodash-es'
import { normalize } from 'normalizr'

import API from '../config'
import { selectUser } from './user'
import { requestAddToCollection } from './userContent'
import { getHeaders } from './api'
import { user as userSchema } from '../schemas'

export const CHANGE_LINK_ORDER = 'CHANGE_LINK_ORDER'
export const REQUEST_CREATE_COLLECTION = 'REQUEST_CREATE_COLLECTION'
export const CREATE_COLLECTION = 'CREATE_COLLECTION'
export const RECEIVE_CREATED_COLLECTION = 'RECEIVE_CREATED_COLLECTION'
export const UPDATE_LINK_ORDER = 'UPDATE_LINK_ORDER'
export const UPDATE_LINK_SORTING = 'UPDATE_LINK_SORTING'
const REQUEST_SAVE_LINK_ORDER = 'REQUEST_SAVE_LINK_ORDER'
const REQUEST_REMOVE_LINK = 'REQUEST_REMOVE_LINK'
export const LINK_REMOVED = 'LINK_REMOVED'
const REQUEST_REMOVE_COLLECTION = 'REQUEST_REMOVE_COLLECTION'
export const COLLECTION_REMOVED = 'COLLECTION_REMOVED'
const REQUEST_COLLECTION_THUMBNAILS = 'REQUEST_COLLECTION_THUMBNAILS'
export const RECEIVE_COLLECTION_THUMBNAILS = 'RECEIVE_COLLECTION_THUMBNAILS'
export const CLEAR_COLLECTION_THUMBNAILS = 'CLEAR_COLLECTION_THUMBNAILS'
export const CLEAR_COLLECTION_CHILDREN_LINKS = 'CLEAR_COLLECTION_CHILDREN_LINKS'

const rootApi = `${API.apiPath}/${API.apiVersion}/`

/** ***************************************************/

export function changeLinkOrder({ collectionId, cardId, position }) {
  return {
    type: CHANGE_LINK_ORDER,
    payload: { collectionId, cardId, position }
  }
}

export function requestSaveLinkOrder(payload) {
  return {
    type: REQUEST_SAVE_LINK_ORDER,
    payload
  }
}

export function requestRemoveLink(payload) {
  return {
    type: REQUEST_REMOVE_LINK,
    payload
  }
}

function linkRemoved(payload) {
  return {
    type: LINK_REMOVED,
    payload
  }
}

export function requestRemoveCollection(payload) {
  return {
    type: REQUEST_REMOVE_COLLECTION,
    payload
  }
}

function collectionRemoved(payload) {
  return {
    type: COLLECTION_REMOVED,
    payload
  }
}

export function requestCollectionThumbnails(payload) {
  return {
    type: REQUEST_COLLECTION_THUMBNAILS,
    payload
  }
}

function receiveCollectionThumbnails(payload) {
  return {
    type: RECEIVE_COLLECTION_THUMBNAILS,
    payload
  }
}

export function clearCollectionThumbnails(payload) {
  return {
    type: CLEAR_COLLECTION_THUMBNAILS,
    payload
  }
}

export function clearCollectionChildrenLinks(payload) {
  return {
    type: CLEAR_COLLECTION_CHILDREN_LINKS,
    payload
  }
}

function* saveLinkOrder(action) {
  const user = yield select(selectUser)
  const token = get(user, 'data.auth_token')
  const { cardIds, collectionId } = action.payload
  yield call(
    [axios, 'post'],
    `deck/bulk-change-links-order`,
    {
      cardIds,
      collectionId
    },
    {
      baseURL: `${API.apiPath}/${API.apiVersion}/`,
      headers: getHeaders(token)
    }
  )
}

function* saveLinkOrderSaga() {
  while (true) {
    const action = yield take(REQUEST_SAVE_LINK_ORDER)
    yield call(saveLinkOrder, action)
  }
}

export function updateCollectionOrder(payload) {
  return {
    type: UPDATE_LINK_ORDER,
    payload
  }
}

export function updateCollectionSort(payload) {
  return {
    type: UPDATE_LINK_SORTING,
    payload
  }
}

export function requestCreateCollection(title, description = '', isPrivate = true, cardToAdd) {
  return {
    type: REQUEST_CREATE_COLLECTION,
    payload: { title, description, isPrivate, cardToAdd }
  }
}

function* processCreateCollection(action) {
  const user = yield select(selectUser)
  const token = get(user, 'data.auth_token')
  const id = get(user, 'data.user.id')
  const { title, description, isPrivate, cardToAdd } = action.payload
  const response = yield call(
    [axios, 'post'],
    `deck/create-empty-deck`,
    {
      title,
      description,
      is_private_pick: isPrivate
    },
    {
      baseURL: `${API.apiPath}/${API.apiVersion}/`,
      headers: getHeaders(token)
    }
  )

  if (response.status && response.status === 200) {
    if (cardToAdd) {
      yield put(requestAddToCollection({ collectionId: response.data.id, pickId: cardToAdd }))
    }
    const structuredResponse = { arr: [response.data] }
    const collections = {}
    collections.public = structuredResponse

    const normalizedData = normalize({ id, collections }, userSchema)
    yield put(receiveCreatedCollection(normalizedData))
  }
}

export function receiveCreatedCollection(data) {
  return {
    type: RECEIVE_CREATED_COLLECTION,
    payload: { data }
  }
}

function* removeLink(action) {
  const user = yield select(selectUser)
  const token = get(user, 'data.auth_token')
  const { collectionId, position } = action.payload
  const response = yield call(
    [axios, 'post'],
    `deck/remove_link_from_collection`,
    {
      deck_id: collectionId,
      link_position: position
    },
    {
      baseURL: `${API.apiPath}/${API.apiVersion}/`,
      headers: getHeaders(token)
    }
  )

  if (response.status && response.status === 200) {
    yield put(linkRemoved(action.payload))
  }
}

function* removeCollection(action) {
  const url = 'deck/delete_cards'
  const { id } = action.payload
  const user = yield select(selectUser)
  const token = get(user, 'data.auth_token')
  const postParams = {
    card_ids: [id]
  }
  try {
    yield call([axios, 'post'], url, postParams, {
      baseURL: `${API.apiPath}/${API.apiVersion}/`,
      headers: getHeaders(token)
    })
    yield put(collectionRemoved({ id, userId: user.data.user.id }))
  } catch (e) {
  } finally {
  }
}

function* getCollectionThumbnails(action) {
  const url = `${rootApi}deck/get-children-thumbnails`
  const { cardId, referrerId, start, limit, thumbnailType } = action.payload
  const user = yield select(selectUser)
  const token = get(user, 'data.auth_token')
  const params = { card_id: cardId, referrer_id: referrerId, start, limit, thumbnail_type: thumbnailType }
  const headers = getHeaders(token)
  try {
    const response = yield call([axios, 'get'], url, { params, headers })
    yield put(receiveCollectionThumbnails({ images: response.data.images, cardId }))
  } catch (e) {
  } finally {
  }
}

function* createCollectionSaga() {
  yield takeLatest(REQUEST_CREATE_COLLECTION, processCreateCollection)
}

function* removeLinkSaga() {
  yield takeLatest(REQUEST_REMOVE_LINK, removeLink)
}

function* removeCollectionSaga() {
  while (true) {
    const action = yield take(REQUEST_REMOVE_COLLECTION)
    yield call(removeCollection, action)
  }
}

function* getCollectionThumbnailsSaga() {
  while (true) {
    const action = yield take(REQUEST_COLLECTION_THUMBNAILS)
    yield call(getCollectionThumbnails, action)
  }
}

export const sagas = [
  createCollectionSaga,
  saveLinkOrderSaga,
  removeLinkSaga,
  removeCollectionSaga,
  getCollectionThumbnailsSaga
]
